import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Users, Search, Plus, Crown, Building2, Phone, Mail } from "lucide-react";
import { toast } from "sonner";
import { useT } from "@/lib/i18n";

export default function Customers() {
  const nav = useNavigate();
  const { t } = useT();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [vipOnly, setVipOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", organization: "", department: "", location: "", notes: "", vip: false, preferred_lang: "en" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (vipOnly) params.set("vip", "true");
      const { data } = await api.get(`/customers${params.toString() ? `?${params}` : ""}`);
      setRows(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q, vipOnly]);

  const create = async () => {
    if (!form.name.trim() || !form.email.trim()) { toast.error("Name and email required"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/customers", { ...form, name: form.name.trim(), email: form.email.trim().toLowerCase() });
      toast.success(`Customer ${data.name} saved`);
      setOpen(false);
      setForm({ name: "", email: "", phone: "", organization: "", department: "", location: "", notes: "", vip: false, preferred_lang: "en" });
      await load();
      nav(`/customers/${data.user_id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-red-600">{t("customers_directory")}</div>
          <h1 className="font-display text-4xl font-black tracking-tight mt-2" data-testid="customers-heading">{t("customers_heading")}</h1>
          <p className="text-slate-600 text-sm mt-2 max-w-lg">{t("customers_intro")}</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="customer-new-btn" className="bg-red-600 hover:bg-red-500 rounded-sm h-11 px-5">
              <Plus className="h-4 w-4" strokeWidth={2} /> {t("customers_new_btn")}
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white border-slate-200 rounded-sm max-w-lg" data-testid="customer-dialog">
            <DialogHeader><DialogTitle className="font-display">{t("customers_create_title")}</DialogTitle></DialogHeader>
            <CustomerForm form={form} set={set} />
            <div className="flex items-center justify-between pt-2">
              <label className="flex items-center gap-2 text-xs text-amber-300 cursor-pointer" data-testid="customer-vip-toggle">
                <input type="checkbox" checked={form.vip} onChange={(e) => set("vip", e.target.checked)} className="accent-amber-400" />
                <Crown className="h-3.5 w-3.5" strokeWidth={1.8} /> {t("customers_mark_vip")}
              </label>
              <Button onClick={create} disabled={saving} data-testid="customer-save-btn" className="bg-red-600 hover:bg-red-500 rounded-sm">
                {saving ? t("loading") : t("customers_save_btn")}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex gap-3 items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="h-4 w-4 absolute left-3 top-3 text-slate-500" strokeWidth={1.5} />
          <Input data-testid="customer-search" placeholder={t("customers_search_ph")} value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 bg-white border-slate-200 rounded-sm h-10" />
        </div>
        <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer border border-slate-200 bg-white px-3 h-10 rounded-sm" data-testid="customer-vip-filter">
          <input type="checkbox" checked={vipOnly} onChange={(e) => setVipOnly(e.target.checked)} className="accent-amber-400" />
          <Crown className="h-3.5 w-3.5 text-amber-400" strokeWidth={1.8} /> {t("customers_vip_only")}
        </label>
        <div className="text-xs text-slate-500 font-mono ml-auto">{t(rows.length === 1 ? "customers_records_n" : "customers_records_p", { n: rows.length })}</div>
      </div>

      <div className="border border-slate-200 bg-white" data-testid="customers-table">
        {loading && <div className="p-8 text-center text-xs text-slate-500 font-mono uppercase tracking-[0.3em]">{t("loading")}</div>}
        {!loading && rows.length === 0 && (
          <div className="p-16 text-center">
            <Users className="h-8 w-8 mx-auto text-slate-700" strokeWidth={1.2} />
            <div className="text-sm text-slate-600 mt-3">{t("customers_empty_title")}</div>
            <div className="text-xs text-slate-600 mt-1">{t("customers_empty_hint")}</div>
          </div>
        )}
        <div className="divide-y divide-slate-800">
          {rows.map((c) => (
            <Link key={c.user_id} to={`/customers/${c.user_id}`} data-testid={`customer-row-${c.email}`} className="flex items-center gap-4 px-5 py-4 hover:bg-slate-100">
              <Avatar className="h-9 w-9 rounded-sm">
                <AvatarFallback className="rounded-sm bg-slate-200 text-xs">{c.name?.slice(0,2).toUpperCase()}</AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-sm">{c.name}</span>
                  {c.vip && <span data-testid="vip-badge" className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-[0.2em] px-1.5 py-0.5 border rounded-sm bg-amber-500/15 text-amber-300 border-amber-500/40"><Crown className="h-2.5 w-2.5" strokeWidth={2} /> VIP</span>}
                </div>
                <div className="text-[11px] text-slate-500 mt-1 font-mono flex items-center gap-3 flex-wrap">
                  <span className="flex items-center gap-1"><Mail className="h-3 w-3" strokeWidth={1.5} />{c.email}</span>
                  {c.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" strokeWidth={1.5} />{c.phone}</span>}
                  {c.organization && <span className="flex items-center gap-1"><Building2 className="h-3 w-3" strokeWidth={1.5} />{c.organization}</span>}
                </div>
              </div>
              <div className="text-right">
                <div className="font-mono text-sm text-slate-700"><span className="text-slate-900 font-bold">{c.ticket_count}</span> {t(c.ticket_count === 1 ? "customers_ticket_n" : "customers_ticket_p", { n: "" }).trim()}</div>
                <div className="text-[10px] font-mono text-slate-500 mt-0.5">{t("customers_open_suffix", { n: c.open_ticket_count })}</div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

export function CustomerForm({ form, set }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
      <div className="md:col-span-2">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1.5">Full name *</div>
        <Input data-testid="customer-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Ana Ximenes" className="bg-white border-slate-200 rounded-sm h-10" />
      </div>
      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1.5">Email *</div>
        <Input data-testid="customer-email-input" value={form.email} onChange={(e) => set("email", e.target.value)} type="email" placeholder="ana@company.tl" className="bg-white border-slate-200 rounded-sm h-10" />
      </div>
      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1.5">Phone</div>
        <Input data-testid="customer-phone-input" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+670 …" className="bg-white border-slate-200 rounded-sm h-10" />
      </div>
      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1.5">Organization</div>
        <Input data-testid="customer-org-input" value={form.organization} onChange={(e) => set("organization", e.target.value)} placeholder="e.g. Ministry of Finance" className="bg-white border-slate-200 rounded-sm h-10" />
      </div>
      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1.5">Department</div>
        <Input data-testid="customer-dept-input" value={form.department} onChange={(e) => set("department", e.target.value)} placeholder="e.g. Payroll" className="bg-white border-slate-200 rounded-sm h-10" />
      </div>
      <div className="md:col-span-2">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1.5">Location</div>
        <Input data-testid="customer-location-input" value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="e.g. Dili HQ · Floor 3" className="bg-white border-slate-200 rounded-sm h-10" />
      </div>
      <div className="md:col-span-2">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1.5">Preferred email language</div>
        <div className="flex gap-2" data-testid="customer-lang-picker">
          <button type="button" onClick={() => set("preferred_lang", "en")} data-testid="cust-lang-en" className={`flex-1 h-10 rounded-sm text-xs font-mono uppercase tracking-[0.2em] border transition-colors ${(form.preferred_lang || "en") === "en" ? "bg-red-500/15 text-red-700 border-red-500/40" : "border-slate-200 text-slate-600 hover:text-slate-900"}`}>EN &middot; English</button>
          <button type="button" onClick={() => set("preferred_lang", "tet")} data-testid="cust-lang-tet" className={`flex-1 h-10 rounded-sm text-xs font-mono uppercase tracking-[0.2em] border transition-colors ${form.preferred_lang === "tet" ? "bg-red-500/15 text-red-700 border-red-500/40" : "border-slate-200 text-slate-600 hover:text-slate-900"}`}>TET &middot; Tetun</button>
        </div>
        <div className="text-[10px] text-slate-600 mt-1">All auto-response emails (acknowledgement, reply-ack, CSAT survey) will be sent in this language.</div>
      </div>
      <div className="md:col-span-2">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1.5">Internal notes (visible to staff only)</div>
        <textarea data-testid="customer-notes-input" value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={3} placeholder="Preferred contact hours, escalation contact, quirks…" className="w-full bg-white border border-slate-200 rounded-sm p-3 text-sm text-slate-900 placeholder:text-slate-600 focus:outline-none focus:border-cyan-500" />
      </div>
    </div>
  );
}
